import { useAuth, useUser } from '@clerk/clerk-react';
import {
  Button,
  PasswordConditions,
  Textfield,
  isPasswordValid,
} from '@flexo/atoms';
import {
  IconWrapper,
  httpService,
  triggerKeychainSave,
} from '@flexo/general';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useFormValidation, getOrgRole, isEndUser } from '@flexo/hooks';
import { ALERT, MODAL, RIGHT_DRAWER, USER } from '@flexo/reducers';
import { Player } from '@lottiefiles/react-lottie-player';
import { useDispatch, useSelector } from 'react-redux';
import styles from './update-credentials-form.module.scss';

const ENV = (import.meta as any).env;

export function UpdateCredentialsForm() {
  const variant = ENV?.VITE_APP_IS_WEB === 'true' ? 'web' : 'account';
  const user = useSelector((state: any) => state.user);
  // FLEXO-6075: end users cannot change their display name (managed centrally);
  // the field is shown but disabled for them. Staff (admin/supervisor) unaffected.
  const isEndUserAccount = isEndUser(getOrgRole(user));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formRef = useRef<any>(null);
  const initializedFromClerk = useRef(false);
  const { user: clerkUser, isLoaded: isClerkLoaded } = useUser();
  const { signOut: clerkSignOut, getToken } = useAuth();

  const { t } = useTranslation();
  const { title, subtitle, inputs, buttonLabel, deleteButtonLabel } = t(
    'drawers.update_credentials',
    { returnObjects: true }
  ) as any;

  const [formData, setFormData] = useState({
    displayName:
      user?.auth?.currentUser?.displayName ||
      '',
    email:
      user?.tokenContent?.firebaseUserResult?.email ||
      '',
    password: '',
    password_confirm: '',
  });

  useEffect(() => {
    if (!initializedFromClerk.current && isClerkLoaded && clerkUser) {
      initializedFromClerk.current = true;
      setFormData((prev) => ({
        ...prev,
        displayName: clerkUser.fullName || prev.displayName || '',
        email: clerkUser.primaryEmailAddress?.emailAddress || prev.email || '',
      }));
    }
  }, [isClerkLoaded, clerkUser]);

  const {
    errors,
    blur,
    passwordBlur,
    passwordValidation,
    handleBlur: hookHandleBlur,
    handleFocus: hookHandleFocus,
    handlePasswordValidation,
  } = useFormValidation(
    {
      fields: ['password', 'password_confirm'],
      hasPasswordFields: true,
    },
    formData
  );

  const [, setFieldBlurred] = useState({
    displayName: false,
    email: false,
  });

  const [formRequestSent, setFormRequestSent] = useState(false);

  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
    password_confirm: false,
  });

  const getInitialDisplayName = () =>
    (clerkUser?.fullName || '').trim() ||
    user?.auth?.currentUser?.displayName ||
    user?.displayName ||
    '';

  const getInitialEmail = () =>
    clerkUser?.primaryEmailAddress?.emailAddress ||
    user?.tokenContent?.firebaseUserResult?.email ||
    user?.email ||
    '';

  const isEmailChanged = () => {
    const initialEmail = getInitialEmail();
    return (
      formData.email.trim().toLowerCase() !==
      (initialEmail || '').trim().toLowerCase()
    );
  };

  const resetEmail = () => {
    const initialEmail = getInitialEmail();
    setFormData((prev) => ({
      ...prev,
      email: initialEmail,
    }));
  };

  const [showSuccessfulAnimation] = useState<boolean>(false);
  const [showForm] = useState<boolean>(true);

  const useAccountStyles = variant === 'account';

  // Check if user is "invited" (not yet verified/activated)
  // Invited users cannot change their email - they must complete registration first
  const isUserInvited = (): boolean => {
    if (!clerkUser) return false;
    // Check public_metadata status
    const metadataStatus = (clerkUser.publicMetadata as any)?.status;
    if (metadataStatus === 'invited') return true;
    // Also check if email is verified (unverified = invited state)
    const emailVerified =
      clerkUser.primaryEmailAddress?.verification?.status === 'verified';
    return !emailVerified;
  };

  async function handleInputChange(event: any) {
    const { name, value } = event.target;

    const updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);

    if (name === 'password') {
      handlePasswordValidation(value, formData.password_confirm);
    }
    if (name === 'password_confirm') {
      handlePasswordValidation(formData.password, value);
    }
  }

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name } = event.target;

    if (name === 'password' || name === 'password_confirm') {
      hookHandleBlur(event);
    } else {
      setFieldBlurred((prev) => ({
        ...prev,
        [name]: true,
      }));
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name } = event.target;

    if (name === 'password' || name === 'password_confirm') {
      hookHandleFocus(event);
    } else {
      setFieldBlurred((prev) => ({
        ...prev,
        [name]: false,
      }));
    }
  };

  function togglePasswordVisibility(
    inputName: keyof typeof passwordVisibility
  ) {
    setPasswordVisibility((prev) => ({
      ...prev,
      [inputName]: !prev[inputName],
    }));
  }

  const getPasswordType = (fieldName: keyof typeof passwordVisibility) =>
    passwordVisibility[fieldName] ? 'text' : 'password';

  const getPasswordIcon = (fieldName: keyof typeof passwordVisibility) =>
    passwordVisibility[fieldName] ? 'show' : 'hide';

  function checkIfSubmitIsDisabled() {
    const initialDisplayName = getInitialDisplayName();
    const initialEmail = getInitialEmail();

    const hasDisplayNameChange =
      formData.displayName.trim() !== initialDisplayName.trim();
    const hasEmailChange =
      formData.email.trim().toLowerCase() !==
      (initialEmail || '').trim().toLowerCase();
    const hasPasswordChange =
      formData.password !== '' || formData.password_confirm !== '';

    const hasChanges =
      hasDisplayNameChange || hasEmailChange || hasPasswordChange;

    if (!hasChanges) {
      return true;
    }

    if (formData.displayName.trim() === '') {
      return true;
    }

    if (hasPasswordChange) {
      if (!passwordValidation.passwordsMatch) {
        return true;
      }

      if (formData.password !== '' && !isPasswordValid(formData.password)) {
        return true;
      }
    }

    return false;
  }

  async function handleEmailUpdate(newEmailRaw: string, currentPassword: string) {
    const normalizedEmail = newEmailRaw.trim();
    const primaryEmail = clerkUser?.primaryEmailAddress?.emailAddress || '';

    if (!normalizedEmail) {
      return {
        requiresVerification: false,
        updatedEmail: primaryEmail || null,
      };
    }

    if (normalizedEmail.toLowerCase() === (primaryEmail || '').toLowerCase()) {
      return {
        requiresVerification: false,
        updatedEmail: primaryEmail || normalizedEmail,
      };
    }

    // Check if user is invited - they cannot change email
    if (isUserInvited()) {
      throw new Error(
        'Invited users cannot change their email. Please complete registration first.'
      );
    }

    // Use hive-api-catalog v1_4 endpoint to change email
    // This endpoint updates both Clerk and Catalog DB to maintain consistency
    const token = await getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // httpService automatically routes /community/catalog/* to VITE_APP_CATALOG_BASE_URL
    const response = await httpService.patch({
      url: '/community/catalog/v1_4/organization/users/me',
      data: { newEmail: normalizedEmail, currentPassword },
      idToken: token,
      skipLogoutOn401: true,
    });

    if (response.status === 401) {
      throw new Error('Invalid current password');
    }

    // Detect "email already in use" from backend error code
    if (response.status === 400) {
      const detail = response.data?.detail || '';
      if (typeof detail === 'string' && detail.includes('ERR_EMAIL_005')) {
        throw new Error('EMAIL_ALREADY_IN_USE');
      }
    }

    if (response.status !== 200) {
      const errorMessage =
        response.data?.detail?.message ||
        response.data?.message ||
        'Failed to change email';
      throw new Error(errorMessage);
    }

    return { requiresVerification: false, updatedEmail: normalizedEmail };
  }

  async function submitForm(currentPassword?: string) {
    try {
      setFormRequestSent(true);
      if (!isClerkLoaded || !clerkUser) {
        console.error('[UpdateCredentialsForm] Clerk user not available');
        return 500;
      }

      const initialDisplayName = getInitialDisplayName();
      const initialEmail = getInitialEmail();

      const trimmedDisplayName = formData.displayName.trim();
      const normalizedEmail = formData.email.trim();

      const displayNameChanged =
        trimmedDisplayName !== initialDisplayName.trim();
      const emailChanged =
        normalizedEmail.toLowerCase() !==
        (initialEmail || '').trim().toLowerCase();
      const passwordChanged = formData.password !== '';

      if (!displayNameChanged && !emailChanged && !passwordChanged) {
        return 200;
      }

      const newUserData = { ...user };
      let requiresEmailVerification = false;

      if (displayNameChanged) {
        const [firstName, ...restNameParts] = trimmedDisplayName.split(' ');
        const lastName = restNameParts.join(' ').trim();

        const updatedUser = await clerkUser.update({
          firstName: firstName || null,
          lastName: lastName || null,
        });

        const resolvedDisplayName = updatedUser.fullName || trimmedDisplayName;

        if (newUserData?.auth?.currentUser?.displayName) {
          newUserData.auth.currentUser.displayName = resolvedDisplayName;
        } else {
          newUserData['auth'] = newUserData['auth'] || {};
          newUserData.auth['currentUser'] =
            newUserData.auth['currentUser'] || {};
          newUserData.auth.currentUser['displayName'] = resolvedDisplayName;
        }

        if (!newUserData['user']) {
          newUserData['user'] = {};
        }
        newUserData.user['displayName'] = resolvedDisplayName;
        newUserData['displayName'] = resolvedDisplayName;

        setFormData((prevData) => ({
          ...prevData,
          displayName: resolvedDisplayName,
        }));
      }

      let normalizedUpdatedEmail = initialEmail;

      if (emailChanged && normalizedEmail) {
        const sanitizedPassword = (currentPassword ?? '').trim();
        if (!sanitizedPassword) {
          return 401;
        }
        const emailOutcome = await handleEmailUpdate(normalizedEmail, sanitizedPassword);
        requiresEmailVerification = emailOutcome.requiresVerification;
        normalizedUpdatedEmail =
          emailOutcome.updatedEmail ?? normalizedUpdatedEmail;

        if (!requiresEmailVerification && normalizedUpdatedEmail) {
          if (newUserData?.auth?.currentUser?.email) {
            newUserData.auth.currentUser.email = normalizedUpdatedEmail;
          } else {
            newUserData['auth'] = newUserData['auth'] || {};
            newUserData.auth['currentUser'] =
              newUserData.auth['currentUser'] || {};
            newUserData.auth.currentUser['email'] = normalizedUpdatedEmail;
          }

          if (newUserData?.tokenContent?.firebaseUserResult) {
            newUserData.tokenContent.firebaseUserResult.email =
              normalizedUpdatedEmail;
          }

          if (!newUserData['user']) {
            newUserData['user'] = {};
          }
          newUserData.user['email'] = normalizedUpdatedEmail;
          newUserData['email'] = normalizedUpdatedEmail;

          setFormData((prevData) => ({
            ...prevData,
            email: normalizedUpdatedEmail,
          }));

          // FLEXO-6087 (#2): keep the password manager / Keychain in sync with
          // the new email. Only when the password is NOT also changing — the
          // password block below already saves the email+new-password pair.
          if (!passwordChanged) {
            triggerKeychainSave(normalizedUpdatedEmail, sanitizedPassword);
          }

          // Email changed successfully
        }
      }

      if (passwordChanged) {
        const sanitizedPassword = (currentPassword ?? '').trim();

        if (!sanitizedPassword) {
          return 401;
        }

        // Use backend endpoint to change password (handles Clerk + SendGrid notification)
        const token = await getToken();
        if (!token) {
          return 401;
        }

        const response = await httpService.post({
          url: `${
            ENV.VITE_APP_CUSTOM_BASE_URL || ENV.VITE_APP_BASE_URL
          }/common/auth/v1/password-change`,
          data: {
            current_password: sanitizedPassword,
            new_password: formData.password,
          },
          idToken: token,
          skipLogoutOn401: true,
        });

        if (response.status === 401) {
          return 401; // Invalid current password
        }

        if (response.status !== 200) {
          throw new Error(
            response.data?.message || 'Failed to change password'
          );
        }

        // Offer the correct email + new password to the password manager /
        // iOS Keychain, overriding any capture triggered by the verification
        // alert's current-password field.
        triggerKeychainSave(
          normalizedUpdatedEmail || formData.email,
          formData.password
        );
      }

      // Refresh Clerk user to pick up email/password changes without logout
      if (emailChanged || passwordChanged) {
        await clerkUser.reload();
      }

      dispatch(USER.SetUserAsync(newUserData) as any);

      dispatch(MODAL.CloseModal());
      dispatch(RIGHT_DRAWER.HideDrawer());
      dispatch(RIGHT_DRAWER.ResetDrawer());

      dispatch(ALERT.ResetAlert());

      if (requiresEmailVerification) {
        dispatch(
          ALERT.SetAlert({
            value: { type: 'BANNER_CHECK_EMAIL' },
          } as any)
        );
      } else if (displayNameChanged || emailChanged || passwordChanged) {
        const successBannerType =
          displayNameChanged && !emailChanged && !passwordChanged
            ? 'BANNER_CHANGE_USERNAME_SUCCESS'
            : 'BANNER_CONFIRM_CREDENTIALS_CHANGES_SUCCESS';

        setTimeout(() => {
          dispatch(
            ALERT.SetAlert({
              value: { type: successBannerType },
            } as any)
          );
        }, 100);
      }

      setFormData((prevData) => ({
        ...prevData,
        password: '',
        password_confirm: '',
      }));

      return 200;
    } catch (error: any) {
      console.error('Error updating credentials with Clerk:', error);

      const errorList: any[] =
        (Array.isArray(error?.errors) && error.errors) ||
        (Array.isArray(error?.data?.errors) && error.data.errors) ||
        [];

      const status =
        typeof error?.status === 'number'
          ? error.status
          : typeof error?.code === 'number'
          ? error.code
          : 500;

      const invalidPassword = errorList.some(
        (item) => item?.code === 'invalid_password'
      );

      if (status === 401 || status === 403 || invalidPassword) {
        return status || 401;
      }

      // Show specific banner for email already in use
      if (error?.message === 'EMAIL_ALREADY_IN_USE') {
        dispatch(
          ALERT.SetAlert({
            value: { type: 'BANNER_EMAIL_ALREADY_IN_USE' },
          } as any)
        );
        return 400;
      }

      dispatch(
        ALERT.SetAlert({
          value: { type: 'BANNER_UNEXPECTED_ERROR' },
        } as any)
      );

      return status;
    } finally {
      setFormRequestSent(false);
    }
  }

  function askForSubmissionConfirmation() {
    const initialEmail = getInitialEmail();

    const hasPasswordChange =
      formData.password.trim() !== '' ||
      formData.password_confirm.trim() !== '';

    const hasEmailChange =
      formData.email.trim().toLowerCase() !==
      (initialEmail || '').trim().toLowerCase();

    // Require current password verification for both password and email changes
    const requiresPassword = hasPasswordChange || hasEmailChange;

    dispatch(ALERT.ResetAlert());
    dispatch(
      ALERT.SetAlert({
        value: {
          type: 'ALERT_CONFIRM_CREDENTIALS_CHANGES',
          requirePassword: requiresPassword,
          callback: async (verifiedPassword?: string) => {
            if (requiresPassword && !verifiedPassword) {
              return 400;
            }
            return await submitForm(verifiedPassword);
          },
        } as any,
      })
    );
  }

  async function deleteAccount(verifiedPassword?: string) {
    if (!verifiedPassword) return 400;

    try {
      const token = await getToken();
      if (!token) return 401;

      // Verify password first
      const verifyResponse = await httpService.post({
        url: `${
          ENV.VITE_APP_CUSTOM_BASE_URL || ENV.VITE_APP_BASE_URL
        }/common/auth/v1/verify-password`,
        data: { password: verifiedPassword },
        idToken: token,
        skipLogoutOn401: true,
      });

      if (!verifyResponse?.data?.verified) {
        return 401;
      }

      // Call delete endpoint (self-delete: pass own clerk user id)
      const organizationId = user?.organizationId || ENV.VITE_APP_ORG_ID;
      const userId = clerkUser?.id;

      if (!organizationId || !userId) return 500;

      const deleteResponse = await httpService.delete({
        url: `${ENV.VITE_APP_BASE_URL}/community/catalog/v1_4/organization/${organizationId}/users/${userId}`,
        mock: false,
      });

      if (deleteResponse.status !== 200) {
        return deleteResponse.status;
      }

      // Success: logout and redirect
      dispatch(MODAL.CloseModal());
      dispatch(ALERT.ResetAlert());
      dispatch(RIGHT_DRAWER.HideDrawer());
      dispatch(RIGHT_DRAWER.ResetDrawer());
      dispatch(USER.ShowSplashScreen());
      dispatch(USER.SetIsLoggingOut());

      try {
        await clerkSignOut();
      } catch (error) {
        console.error('Clerk signout failed:', error);
      }

      dispatch(USER.SetLogout());
      navigate('/login');

      return 200;
    } catch (error) {
      console.error('Error deleting account:', error);
      dispatch(
        ALERT.SetAlert({
          value: { type: 'BANNER_UNEXPECTED_ERROR' },
        } as any)
      );
      return 500;
    }
  }

  function handleDeleteAccount() {
    dispatch(ALERT.ResetAlert());
    dispatch(
      ALERT.SetAlert({
        value: {
          type: 'ALERT_CONFIRM_DELETE_ACCOUNT',
          requirePassword: true,
          callback: async (verifiedPassword?: string) => {
            return await deleteAccount(verifiedPassword);
          },
        } as any,
      })
    );
  }

  function close() {
    dispatch(RIGHT_DRAWER.ResetDrawer());
  }

  function focusChild(event: any) {
    try {
      const parent = event.target;
      const child = parent.querySelector('input');
      child.focus();
    } catch (err: any) {
      console.warn(err);
    }
  }

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        close();
      }
      if (
        event.key === 'Enter' &&
        formRef?.current?.contains(document.activeElement)
      ) {
        event.preventDefault();
        if (!checkIfSubmitIsDisabled()) {
          askForSubmissionConfirmation();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [formRequestSent, checkIfSubmitIsDisabled]);

  useEffect(() => {
    if (ENV?.VITE_APP_IS_WEB !== 'true') {
      const handleFocus = (event: FocusEvent) => {
        (event.target as HTMLElement).scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      };
      const inputs = document.querySelectorAll('input');
      inputs.forEach((input) => {
        input.addEventListener('focus', handleFocus);
      });
      return () => {
        inputs.forEach((input) => {
          input.removeEventListener('focus', handleFocus);
        });
      };
    }
  }, []);

  return (
    <div
      className={
        useAccountStyles ? styles.Account : styles.UpdateCredentialsForm
      }
    >
      {showForm ? (
        <>
          {variant !== 'account' && (
            <div
              className={
                useAccountStyles
                  ? undefined
                  : styles.UpdateCredentialsForm__Header
              }
            >
              <div className={styles.UpdateCredentialsForm__Header__Title}>
                {title}
                <div
                  className={styles.UpdateCredentialsForm__Header__Title__Close}
                  onClick={() => close()}
                >
                  <IconWrapper iconName={'cross'} />
                </div>
              </div>
              <div className={styles.UpdateCredentialsForm__Header__Subtitle}>
                {subtitle}
              </div>
            </div>
          )}
          <form
            id={`account_creation_${variant}_form`}
            className={
              useAccountStyles
                ? styles.Account__Information__Textfield__Wrapper
                : styles.UpdateCredentialsForm__Form
            }
            ref={formRef}
            style={useAccountStyles ? { marginTop: 30, paddingLeft: 4 } : {}}
            onSubmit={(e) => {
              e.preventDefault();
              if (!checkIfSubmitIsDisabled()) {
                askForSubmissionConfirmation();
              }
            }}
          >
            {/* Hidden email field for keychain/password manager capture */}
            <input
              type="email"
              name="email"
              autoComplete="username"
              value={formData.email}
              readOnly
              style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
              tabIndex={-1}
            />
            <div
              className={
                useAccountStyles
                  ? styles.Account__Information__Textfield
                  : styles.UpdateCredentialsForm__Form__Input
              }
            >
              <Textfield
                label={inputs.generalities.label}
                name={inputs.generalities.name}
                type={inputs.generalities.type}
                placeholder={inputs.generalities.placeholder}
                variant="default"
                disabled={isEndUserAccount}
                onPaste={(e) => false}
                onCopy={(e) => false}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                autoComplete="off"
                defaultValue={formData[inputs.generalities.name]}
                onChange={(ev: any) => handleInputChange(ev)}
                onBlur={handleBlur}
                onFocus={handleFocus}
                containedLabel={useAccountStyles ? true : undefined}
                color="white"
              />
            </div>

            <div
              className={
                useAccountStyles
                  ? styles.Account__Information__Textfield
                  : isUserInvited()
                  ? `${styles.UpdateCredentialsForm__Form__Input} ${styles.UpdateCredentialsForm__Form__Input__Disabled}`
                  : styles.UpdateCredentialsForm__Form__Input
              }
            >
              <Textfield
                variant="default"
                label={inputs.email.label}
                name={inputs.email.name}
                type={inputs.email.type}
                placeholder={inputs.email.placeholder}
                color={'white'}
                disabled={isUserInvited()}
                onPaste={(e) => false}
                onCopy={(e) => false}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                autoComplete="off"
                value={formData[inputs.email.name]}
                onChange={(ev: any) => handleInputChange(ev)}
                onBlur={handleBlur}
                onFocus={handleFocus}
                additionalLabelStyles={
                  styles.UpdateCredentialsForm__Form__Input__UpperLabel
                }
                containedLabel={false}
                icon={isEmailChanged() ? 'refresh' : undefined}
                onIconClick={isEmailChanged() ? resetEmail : undefined}
              />
            </div>

            <div
              className={
                useAccountStyles
                  ? styles.Account__Information__Textfield
                  : styles.UpdateCredentialsForm__Form__Input
              }
              onClick={(e) => focusChild(e)}
            >
              <Textfield
                variant="default"
                label={inputs.password.label}
                color="white"
                name={inputs.password.name}
                type={getPasswordType(inputs.password.name)}
                placeholder={inputs.password.placeholder}
                onPaste={(e) => false}
                onCopy={(e) => false}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                autoComplete="new-password"
                onChange={(ev: any) => handleInputChange(ev)}
                onBlur={handleBlur}
                onFocus={handleFocus}
                icon={getPasswordIcon(inputs.password.name)}
                onIconClick={() =>
                  togglePasswordVisibility(inputs.password.name)
                }
                containedLabel={useAccountStyles ? true : undefined}
                value={formData[inputs.password.name]}
              />
            </div>

            <div
              className={
                useAccountStyles
                  ? styles.Account__Information__Textfield
                  : styles.UpdateCredentialsForm__Form__Input
              }
              style={useAccountStyles ? { marginBottom: 0 } : {}}
            >
              <div
                className={styles.UpdateCredentialsForm__Form__Checklist__Title}
              >
                {inputs.checkList_title}
              </div>
              <PasswordConditions
                validation={passwordValidation}
                isFocused={!passwordBlur}
                className={
                  styles.UpdateCredentialsForm__Form__PasswordConditions
                }
              />
            </div>

            <div
              className={
                useAccountStyles
                  ? styles.Account__Information__Textfield
                  : styles.UpdateCredentialsForm__Form__Input
              }
              onClick={(e) => focusChild(e)}
            >
              <Textfield
                variant="default"
                label={inputs.password_confirm.label}
                color="white"
                name={inputs.password_confirm.name}
                type={getPasswordType(inputs.password_confirm.name)}
                placeholder={inputs.password_confirm.placeholder}
                onPaste={(e) => false}
                onCopy={(e) => false}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                autoComplete="off"
                onChange={handleInputChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
                icon={getPasswordIcon(inputs.password_confirm.name)}
                onIconClick={() =>
                  togglePasswordVisibility(inputs.password_confirm.name)
                }
                underline={
                  errors.password_confirm && blur.password_confirm
                    ? 'visible'
                    : 'hidden'
                }
                webError={errors.password_confirm && blur.password_confirm}
                error={
                  errors.password_confirm && blur.password_confirm
                    ? inputs.password_confirm.descriptionFault
                    : ''
                }
                containedLabel={useAccountStyles ? true : undefined}
                value={formData[inputs.password_confirm.name]}
              />
            </div>
          </form>
          <div
            className={
              useAccountStyles ? '' : styles.UpdateCredentialsForm__Footer
            }
          >
            <Button
              variant="secondary"
              color="negative"
              className={
                ENV?.VITE_APP_IS_WEB !== 'true'
                  ? styles.UpdateCredentialsForm__Footer__Button__Delete__Mobile
                  : styles.UpdateCredentialsForm__Footer__Button__Delete
              }
              label={deleteButtonLabel}
              onClick={() => handleDeleteAccount()}
            />
            <Button
              variant="primary"
              color="secondary"
              type="submit"
              form={`account_creation_${variant}_form`}
              className={
                ENV?.VITE_APP_IS_WEB !== 'true'
                  ? styles.UpdateCredentialsForm__Footer__Button__Mobile
                  : styles.UpdateCredentialsForm__Footer__Button
              }
              label={buttonLabel}
              disabled={checkIfSubmitIsDisabled()}
              style={useAccountStyles ? { width: '100%', minHeight: 48 } : {}}
            />
          </div>
        </>
      ) : (
        showSuccessfulAnimation && (
          <div className={styles.UpdateCredentialsForm__Success__Container}>
            <div className={styles.UpdateCredentialsForm__Success}>
              <Player
                autoplay
                loop={false}
                src={'/trophyAnimation.json'}
                style={{ height: 150, marginBottom: 50 }}
              />
              <span
                className={`${styles.UpdateCredentialsForm__Success__Title} heading3`}
              >
                {t('modals.updateCredentialsForm.successTitle')}
              </span>
              <span
                className={`${styles.UpdateCredentialsForm__Success__Subtitle} paragraph`}
              >
                {t('modals.updateCredentialsForm.successSubtitle')}
              </span>
            </div>
          </div>
        )
      )}
    </div>
  );
}
